import { Cluster, Extraction, Polyline } from '@editor/services/PolylineExtraction/interfaces'
import { Vector3Like } from 'three'

import {
  buildSpatialHash,
  computePrincipalDirection,
  dist,
  distSq,
  dot,
  interleavedToVec3,
  neighborBucketKeys,
  normalize,
  sub,
} from '../../utils/math'

export interface DbScanConfig {
  /**
   * Neighborhood radius (world units).
   * Larger = merges more points into the same cluster.
   */
  eps?: number

  /**
   * Minimum points required to form a cluster.
   * Larger = fewer clusters.
   */
  minPts?: number

  /**
   * RANSAC distance threshold (world units).
   * Smaller = stricter; fewer inliers kept but more precise.
   */
  ransacThreshold?: number

  /**
   * Max number of RANSAC trials.
   * Higher = more stable line detection, but slower.
   */
  ransacIterations?: number

  /**
   * Douglas–Peucker tolerance (world units).
   * Larger = more simplification (fewer points, straighter).
   */
  douglasPeuckerEpsilon?: number

  /**
   * Minimum RANSAC inliers required to accept a line.
   */
  minInliers?: number

  /**
   * Gap factor multiplier for splitting polylines.
   */
  maxGapFactor?: number

  /**
   * Maximum number of RANSAC iterations per cluster.
   */
  maxRansacPerCluster?: number

  /**
   * Disable gap splitting within RANSAC results.
   */
  disableGapSplitting?: boolean

  /**
   * Enable PCA warm-start for RANSAC.
   */
  useWarmStart?: boolean

  /**
   * Enable direction binning before RANSAC.
   */
  useDirectionBinning?: boolean

  /**
   * Azimuth bin count for direction binning.
   */
  dirBinsAzimuth?: number

  /**
   * Elevation bin count for direction binning.
   */
  dirBinsElevation?: number

  /**
   * Median distance threshold for cluster acceptance (world units).
   * - `ratio = max / median` compares the most distant point in the cluster to the
   *   "typical" distance (median) from the centroid.
   * - A ratio <= 1 indicates a very compact, roughly spherical cluster where all
   *   points lie at similar distances from the centroid; in practice this tends
   *   to be isolated noise rather than a meaningful crack segment.
   * - A ratio > 1 indicates an elongated / anisotropic cluster, where at least
   *   one point is noticeably farther from the centroid than the bulk of the
   *   points. Empirically, this correlates well with crack-like structures,
   *   so we use 1 as a conservative fixed threshold here.
   */
  medianDistanceThreshold: number
}

/**
 * DBSCAN-based polyline extraction
 * Clusters points using DBSCAN, then applies RANSAC to extract polylines from each cluster
 */
export class DbScan extends Extraction {
  protected config: Required<DbScanConfig>

  constructor(config?: DbScanConfig) {
    super()
    this.config = {
      eps: 0.005,
      minPts: 12,
      ransacThreshold: 0.05,
      ransacIterations: 1000,
      douglasPeuckerEpsilon: 0.002,
      minInliers: 10,
      maxGapFactor: 4.0,
      maxRansacPerCluster: 1,
      disableGapSplitting: true,
      useWarmStart: true,
      useDirectionBinning: true,
      dirBinsAzimuth: 8,
      dirBinsElevation: 4,
      medianDistanceThreshold: 1.0,
      ...config,
    }
    this.validateConfig()
  }

  getName(): string {
    return `DbScan(eps=${(this.config.eps * 1000).toFixed(1)}mm, minPts=${this.config.minPts})`
  }

  run(points: Float32Array): Polyline[] {
    if (points.length === 0) return []

    const pts = interleavedToVec3(points)
    this.logger?.log(
      `    🔗 DBSCAN clustering: ${pts.length} points, eps=${(this.config.eps * 1000).toFixed(1)}mm, minPts=${this.config.minPts}`,
    )

    // Run DBSCAN clustering
    const clusters = this.dbscan(pts)
    this.logger?.log(`    ✅ Found ${clusters.length} clusters`)

    // Extract polylines from each cluster
    const polylines: Polyline[] = []
    let totalExtracted = 0

    clusters.forEach((cluster) => {
      const extracted = this.generatePolylineFromCluster(cluster)
      totalExtracted += extracted.length

      if (extracted.length > 1) {
        this.logger?.log(`        ⚠️  Multiple polylines from one cluster: ${extracted.length} polylines`)
      } else if (!extracted.length) {
        this.logger?.log(`        ❌ No polylines extracted from cluster`)
      }

      polylines.push(...extracted)
    })

    this.logger?.log(`    ✅ Extraction complete: ${totalExtracted} polylines from ${clusters.length} clusters`)
    return polylines
  }

  private dbscan(pts: Vector3Like[]): Cluster[] {
    const labels = new Array(pts.length).fill(-1)
    let clusterId = 0
    const epsSq = this.config.eps * this.config.eps

    // Build spatial hash for performance
    const { map, keyOf } = buildSpatialHash(pts, this.config.eps)

    const regionQuery = (i: number): number[] => {
      const p = pts[i]
      const [kx, ky, kz] = keyOf(p)
        .split('_')
        .map((v: string) => parseInt(v, 10))
      const keys = neighborBucketKeys(kx, ky, kz)
      const neighbors: number[] = []

      keys.forEach((k: string) => {
        const arr = map.get(k)
        if (arr) {
          arr.forEach((j: number) => {
            if (i !== j && distSq(pts[j], p) <= epsSq) {
              neighbors.push(j)
            }
          })
        }
      })

      return neighbors
    }

    const expandCluster = (i: number, neighbors: number[]): void => {
      labels[i] = clusterId
      const queue = [...neighbors]

      while (queue.length > 0) {
        const j = queue.shift()
        if (j !== undefined && labels[j] === -1) {
          labels[j] = clusterId
          const n2 = regionQuery(j)
          if (n2.length >= this.config.minPts) {
            const newNeighbors = n2.filter((idx: number) => labels[idx] === -1)
            queue.push(...newNeighbors)
          }
        }
      }
    }

    // Main DBSCAN loop
    pts.forEach((p: Vector3Like, i: number) => {
      if (labels[i] !== -1) return // Already processed

      const neighbors = regionQuery(i)
      if (neighbors.length < this.config.minPts) {
        labels[i] = 0 // Mark as noise
      } else {
        clusterId += 1
        expandCluster(i, neighbors)
      }
    })

    // Build clusters from labels
    const clustersPoints: Vector3Like[][] = Array.from({ length: clusterId }, () => [])
    pts.forEach((p: Vector3Like, i: number) => {
      const id = labels[i] as number
      if (id > 0) {
        clustersPoints[id - 1].push(p)
      }
    })

    // Calculate centroids
    const clusters = clustersPoints
      .map<Cluster>((points: Vector3Like[]) => {
        const centroid = points.reduce(
          (acc, p) => ({
            x: acc.x + p.x,
            y: acc.y + p.y,
            z: acc.z + p.z,
          }),
          { x: 0, y: 0, z: 0 },
        )

        this.logger?.log(
          `        🟢 Cluster with ${points.length} points, centroid=(${(centroid.x / points.length).toFixed(3)}, ${(centroid.y / points.length).toFixed(3)}, ${(centroid.z / points.length).toFixed(3)})`,
        )

        return {
          centroid: {
            x: centroid.x / points.length,
            y: centroid.y / points.length,
            z: centroid.z / points.length,
          },
          points,
        }
      })
      .reduce<Cluster[]>((acc, { points, centroid }) => {
        if (!centroid) return acc

        // Foreach cluster, calculate the distances of points to centroid to see how much
        // of spread there is. Less spread = isolated noise, more spread = meaningful structure.
        const distances = points.map((p) => dist(p, centroid))
        const sorted = distances.slice().sort((a, b) => a - b)
        const median =
          sorted.length % 2 === 0
            ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
            : sorted[Math.floor(sorted.length / 2)]
        const avg = distances.reduce((s, v) => s + v, 0) / distances.length
        const max = Math.max(...distances)
        const ratio = max / median
        const endToEndDist = dist(points[0], points[points.length - 1])

        // If the max distance is significantly larger than median, it's likely a meaningful cluster
        const dbg = `(max=${max.toFixed(3)}, median=${median.toFixed(3)}, avg=${avg.toFixed(3)}, ratio=${ratio.toFixed(3)}, endToEnd=${(endToEndDist * 1000).toFixed(3)}mm, points=${points.length})`
        if (ratio > this.config.medianDistanceThreshold) {
          this.logger?.log(`        ✔️  Accepted cluster ${dbg}`)
          acc.push({ points, centroid })
        } else {
          this.logger?.log(`        ❌ Rejected cluster ${dbg}`)
        }

        return acc
      }, [])

    // Calculate centroids and return Clusters
    return clusters
  }

  private generatePolylineFromCluster(cluster: Cluster): Polyline[] {
    const { points } = cluster
    if (points.length === 0) return []

    const polylines: Polyline[] = []
    const { minInliers = 6, maxGapFactor = 3.0 } = this.config

    // Extract polylines using RANSAC
    const extractFromIndices = (indices: number[]): void => {
      let iterationCount = 0
      const subset = indices.map((i: number) => points[i])
      let localRemaining = subset.slice()

      while (localRemaining.length >= minInliers && iterationCount < (this.config.maxRansacPerCluster || 10)) {
        iterationCount += 1

        // Try RANSAC with optional PCA warm-start
        let dirHint: Vector3Like | undefined
        let hintPoint: Vector3Like | undefined
        if (this.config.useWarmStart) {
          dirHint = computePrincipalDirection(localRemaining)
          hintPoint = localRemaining[Math.floor(localRemaining.length / 2)]
        }

        const best = this.ransacLine(localRemaining, {
          directionHint: dirHint,
          hintPoint,
          earlyExitRatio: 0.6,
        })

        if (!best || best.inliers.length < minInliers) {
          break
        }

        const ordered = DbScan.orderAlongLine(localRemaining, best.linePoint, best.dir, best.inliers)
        const segments = this.config.disableGapSplitting ? [ordered] : DbScan.splitByGaps(ordered, maxGapFactor)

        segments.forEach((seg: Vector3Like[]) => {
          const simplified = DbScan.simplifyDP(seg, this.config.douglasPeuckerEpsilon)
          if (simplified.length >= 2) {
            polylines.push(simplified)
          }
        })

        // Remove inliers for next iteration by building new array
        const inlierSet = new Set(best.inliers)
        localRemaining = localRemaining.filter((_, i) => !inlierSet.has(i))
      }
    }

    // Apply direction binning if enabled
    if (this.config.useDirectionBinning) {
      const bins = this.createDirectionBins(points)
      Array.from(bins.values()).forEach((idxs: number[]) => {
        if (idxs.length >= minInliers) {
          extractFromIndices(idxs)
        }
      })
    } else {
      extractFromIndices(Array.from({ length: points.length }, (_, i) => i))
    }

    return polylines
  }

  private ransacLine(
    pts: Vector3Like[],
    options?: {
      directionHint?: Vector3Like
      hintPoint?: Vector3Like
      earlyExitRatio?: number
    },
  ): { linePoint: Vector3Like; dir: Vector3Like; inliers: number[] } | null {
    if (pts.length < 2) return null

    let bestInliers: number[] = []
    let bestLine: { p: Vector3Like; d: Vector3Like } | null = null
    const earlyExitRatio = options?.earlyExitRatio ?? 0.6

    // Try warm-start candidate first
    if (options?.directionHint && options?.hintPoint) {
      const inliers = this.findInliers(pts, options.hintPoint, options.directionHint)
      if (inliers.length > bestInliers.length) {
        bestInliers = inliers
        bestLine = { p: options.hintPoint, d: options.directionHint }
      }
    }

    // RANSAC iterations
    for (let it = 0; it < this.config.ransacIterations; it += 1) {
      if (bestInliers.length >= pts.length * earlyExitRatio) break

      const i1 = Math.floor(Math.random() * pts.length)
      let i2 = Math.floor(Math.random() * pts.length)
      while (i2 === i1) i2 = Math.floor(Math.random() * pts.length)

      const p1 = pts[i1]
      const p2 = pts[i2]
      const dir = normalize(sub(p2, p1))

      const inliers = this.findInliers(pts, p1, dir)
      if (inliers.length > bestInliers.length) {
        bestInliers = inliers
        bestLine = { p: p1, d: dir }
      }
    }

    return bestLine ? { linePoint: bestLine.p, dir: bestLine.d, inliers: bestInliers } : null
  }

  private findInliers(pts: Vector3Like[], linePoint: Vector3Like, dir: Vector3Like): number[] {
    const inliers: number[] = []
    pts.forEach((p: Vector3Like, i: number) => {
      const distance = DbScan.distancePointToLine(p, linePoint, dir)
      if (distance <= this.config.ransacThreshold) {
        inliers.push(i)
      }
    })
    return inliers
  }

  private static distancePointToLine(p: Vector3Like, p0: Vector3Like, dir: Vector3Like): number {
    const v = sub(p, p0)
    const proj = dot(v, dir)
    const perpSq = Math.max(0, dot(v, v) - proj * proj)
    return Math.sqrt(perpSq)
  }

  private static orderAlongLine(
    pts: Vector3Like[],
    lineP: Vector3Like,
    dir: Vector3Like,
    indices: number[],
  ): Vector3Like[] {
    const indexed = indices.map((i: number) => ({
      point: pts[i],
      t: dot(sub(pts[i], lineP), dir),
    }))
    indexed.sort((a, b) => a.t - b.t)
    return indexed.map((item) => item.point)
  }

  private static splitByGaps(ordered: Vector3Like[], maxGapFactor: number): Vector3Like[][] {
    if (ordered.length < 2) return [ordered]

    const gaps: number[] = []
    for (let i = 1; i < ordered.length; i += 1) {
      gaps.push(dist(ordered[i - 1], ordered[i]))
    }

    const mean = gaps.reduce((s, v) => s + v, 0) / gaps.length
    const threshold = mean * maxGapFactor

    const segments: Vector3Like[][] = []
    let cur: Vector3Like[] = [ordered[0]]

    for (let i = 1; i < ordered.length; i += 1) {
      if (gaps[i - 1] > threshold) {
        segments.push(cur)
        cur = [ordered[i]]
      } else {
        cur.push(ordered[i])
      }
    }
    if (cur.length) segments.push(cur)

    return segments
  }

  private static simplifyDP(pts: Vector3Like[], epsilon: number): Vector3Like[] {
    if (pts.length < 3) return pts.slice()

    const simplifyRecursive = (start: number, end: number): number[] => {
      let maxDist = -1
      let maxIndex = -1

      for (let i = start + 1; i < end; i += 1) {
        const d = DbScan.distancePointToSegment(pts[i], pts[start], pts[end])
        if (d > maxDist) {
          maxDist = d
          maxIndex = i
        }
      }

      if (maxDist > epsilon) {
        const left = simplifyRecursive(start, maxIndex)
        const right = simplifyRecursive(maxIndex, end)
        return [...left.slice(0, -1), ...right]
      }

      return [start, end]
    }

    const indices = simplifyRecursive(0, pts.length - 1)
    return indices.map((i: number) => pts[i])
  }

  private static distancePointToSegment(p: Vector3Like, a: Vector3Like, b: Vector3Like): number {
    const ab = sub(b, a)
    const ap = sub(p, a)
    const abLengthSq = dot(ab, ab)

    // Handle degenerate case where a and b are the same point
    if (abLengthSq === 0) {
      return dist(p, a)
    }

    const proj = dot(ap, ab) / abLengthSq

    if (proj < 0) return dist(p, a)
    if (proj > 1) return dist(p, b)

    const closest = { x: a.x + proj * ab.x, y: a.y + proj * ab.y, z: a.z + proj * ab.z }
    return dist(p, closest)
  }

  private createDirectionBins(pts: Vector3Like[]): Map<string, number[]> {
    const bins = new Map<string, number[]>()
    const { dirBinsAzimuth = 8, dirBinsElevation = 4 } = this.config

    // Build spatial hash for local neighborhoods
    const cell = this.config.ransacThreshold * 2
    const { map, keyOf } = buildSpatialHash(pts, cell)

    pts.forEach((p: Vector3Like, i: number) => {
      const [kx, ky, kz] = keyOf(p)
        .split('_')
        .map((v: string) => parseInt(v, 10))
      const keys = neighborBucketKeys(kx, ky, kz)
      const neigh: number[] = []

      keys.forEach((k: string) => {
        const arr = map.get(k)
        if (arr) {
          arr.forEach((j: number) => neigh.push(j))
        }
      })

      const dir = computePrincipalDirection(pts, neigh.length ? neigh : undefined)
      const key = DbScan.quantizeDirection(dir, dirBinsAzimuth, dirBinsElevation)

      const arr = bins.get(key)
      if (arr) arr.push(i)
      else bins.set(key, [i])
    })

    return bins
  }

  private static quantizeDirection(d: Vector3Like, azBins: number, elBins: number): string {
    const r = Math.hypot(d.x, d.y, d.z) || 1
    const theta = Math.atan2(d.y, d.x)
    const phi = Math.acos(Math.max(-1, Math.min(1, d.z / r)))

    const a = Math.floor(((theta + Math.PI) / (2 * Math.PI)) * azBins)
    const e = Math.floor((phi / Math.PI) * elBins)

    return `${a}_${e}`
  }

  private validateConfig(): void {
    if (this.config.eps <= 0) {
      throw new Error('[DbScan] eps must be positive')
    }
    if (this.config.minPts < 1) {
      throw new Error('[DbScan] minPts must be at least 1')
    }
    if (this.config.ransacThreshold <= 0) {
      throw new Error('[DbScan] ransacThreshold must be positive')
    }
    if (this.config.ransacIterations < 1) {
      throw new Error('[DbScan] ransacIterations must be at least 1')
    }
    if (this.config.douglasPeuckerEpsilon < 0) {
      throw new Error('[DbScan] douglasPeuckerEpsilon must be non-negative')
    }
  }
}
